import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowLeft,
  CheckCircle2,
  Database,
  KeyRound,
  Loader2,
  Power,
  RefreshCw,
  RotateCcw,
  Save,
  XCircle,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { getAdminToken } from '@/api/adminApi'
import { restartAdminServer } from '@/api/adminBackupApi'
import { ApiError, friendlyMessage } from '@/api/http'
import {
  deleteStorageConfig,
  getStorageConfig,
  putStorageConfig,
  testStorage,
  type StorageConfigPayload,
  type StorageConfigResponse,
  type TestStorageResponse,
} from '@/api/storageConfigApi'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

type StorageBackend = StorageConfigPayload['backend']

interface StorageConfigForm {
  backend: StorageBackend
  s3_endpoint: string
  s3_region: string
  presign_ttl_sec: string
  buckets: StorageConfigPayload['buckets']
  local: StorageConfigPayload['local']
  s3_access_key: string
  s3_secret_key: string
}

const LOCAL_ROOT_LABEL_KEYS: Record<keyof StorageConfigForm['local'], string> = {
  image_root: 'storageConfig.local.imageRoot',
  audio_root: 'storageConfig.local.audioRoot',
  video_root: 'storageConfig.local.videoRoot',
  text_root: 'storageConfig.local.textRoot',
}

function configToForm(config: StorageConfigResponse): StorageConfigForm {
  return {
    backend: config.backend.toLowerCase() === 's3' ? 's3' : 'local',
    s3_endpoint: config.s3_endpoint,
    s3_region: config.s3_region,
    presign_ttl_sec: String(config.presign_ttl_sec),
    buckets: { ...config.buckets },
    local: { ...config.local_roots },
    s3_access_key: '',
    s3_secret_key: '',
  }
}

function formToPayload(form: StorageConfigForm): StorageConfigPayload {
  const accessKey = form.s3_access_key.trim()
  const secretKey = form.s3_secret_key.trim()
  const ttl = Number(form.presign_ttl_sec)

  return {
    backend: form.backend,
    s3_endpoint: form.s3_endpoint,
    s3_region: form.s3_region,
    presign_ttl_sec: Number.isFinite(ttl) ? ttl : 0,
    buckets: { ...form.buckets },
    local: { ...form.local },
    ...(accessKey ? { s3_access_key: form.s3_access_key } : {}),
    ...(secretKey ? { s3_secret_key: form.s3_secret_key } : {}),
  }
}

function storageErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.message) return error.message
  return friendlyMessage(error, fallback)
}

export default function AdminStorageConfig() {
  const { t } = useTranslation()
  const hasAdminToken = Boolean(getAdminToken())
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [config, setConfig] = useState<StorageConfigResponse | null>(null)
  const [form, setForm] = useState<StorageConfigForm | null>(null)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [resetting, setResetting] = useState(false)
  const [restarting, setRestarting] = useState(false)
  const [restartRequired, setRestartRequired] = useState(false)
  const [testResult, setTestResult] = useState<TestStorageResponse | null>(null)

  const loadConfig = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const nextConfig = await getStorageConfig()
      setConfig(nextConfig)
      setForm(configToForm(nextConfig))
    } catch (error) {
      setLoadError(friendlyMessage(error, t('common.serviceError')))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    if (!hasAdminToken) return
    void loadConfig()
  }, [hasAdminToken, loadConfig])

  const updateForm = <K extends keyof StorageConfigForm>(key: K, value: StorageConfigForm[K]) => {
    setForm((current) => (current ? { ...current, [key]: value } : current))
    setTestResult(null)
  }

  const updateBucket = (key: keyof StorageConfigForm['buckets'], value: string) => {
    setForm((current) => (current ? { ...current, buckets: { ...current.buckets, [key]: value } } : current))
    setTestResult(null)
  }

  const updateLocalRoot = (key: keyof StorageConfigForm['local'], value: string) => {
    setForm((current) => (current ? { ...current, local: { ...current.local, [key]: value } } : current))
    setTestResult(null)
  }

  const onSave = async () => {
    if (!form) return
    setSaving(true)
    try {
      const payload = formToPayload(form)
      const result = await putStorageConfig(payload)
      setConfig((current) =>
        current
          ? {
              ...current,
              backend: payload.backend,
              s3_endpoint: payload.s3_endpoint,
              s3_region: payload.s3_region,
              presign_ttl_sec: payload.presign_ttl_sec,
              buckets: payload.buckets,
              local_roots: payload.local,
              s3_credentials_configured:
                current.s3_credentials_configured || Boolean(payload.s3_access_key && payload.s3_secret_key),
            }
          : current,
      )
      setForm((current) =>
        current ? { ...current, s3_access_key: '', s3_secret_key: '' } : current,
      )
      setRestartRequired(result.restart_required)
      toast.success(t('storageConfig.toast.saveSuccess'))
    } catch (error) {
      toast.error(storageErrorMessage(error, t('storageConfig.toast.saveFailed')))
    } finally {
      setSaving(false)
    }
  }

  const onTest = async () => {
    if (!form) return
    setTesting(true)
    setTestResult(null)
    try {
      const payload = formToPayload(form)
      const result = await testStorage(payload)
      setTestResult(result)
      if (result.ok) toast.success(t('storageConfig.testPassed'))
      else toast.error(result.error || t('storageConfig.testFailed'))
    } catch (error) {
      const message = storageErrorMessage(error, t('storageConfig.testFailed'))
      setTestResult({ ok: false, backend: form.backend, bucket: '', error: message })
      toast.error(message)
    } finally {
      setTesting(false)
    }
  }

  const onReset = async () => {
    if (!window.confirm(t('storageConfig.resetConfirm'))) return
    setResetting(true)
    try {
      const result = await deleteStorageConfig()
      setRestartRequired(result.restart_required)
      toast.success(t('storageConfig.toast.resetSuccess'))
      await loadConfig()
    } catch (error) {
      toast.error(storageErrorMessage(error, t('storageConfig.toast.resetFailed')))
    } finally {
      setResetting(false)
    }
  }

  const onRestart = async () => {
    setRestarting(true)
    try {
      await restartAdminServer()
      setRestartRequired(false)
      toast.success(t('storageConfig.toast.restartSuccess'))
    } catch (error) {
      toast.error(storageErrorMessage(error, t('storageConfig.toast.restartFailed')))
    } finally {
      setRestarting(false)
    }
  }

  if (!hasAdminToken) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-8 sm:px-6">
        <Card className="rounded-lg">
          <CardHeader>
            <CardTitle className="text-base">{t('storageConfig.tokenMissingTitle')}</CardTitle>
            <CardDescription>{t('storageConfig.tokenMissingDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <Link to="/admin">{t('storageConfig.goAdmin')}</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-8 sm:px-6">
      <div className="space-y-2">
        <Button asChild className="-ml-2 h-8 px-2 text-muted-foreground" size="sm" variant="ghost">
          <Link to="/admin">
            <ArrowLeft className="mr-1 size-4" />
            {t('storageConfig.backToAdmin')}
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold tracking-normal">{t('storageConfig.title')}</h1>
        <p className="max-w-3xl text-sm text-muted-foreground">{t('storageConfig.subtitle')}</p>
      </div>

      {restartRequired ? (
        <Card className="rounded-lg border-amber-500/40 bg-amber-500/5">
          <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2 font-medium">
                <Power className="size-4 text-amber-600" />
                {t('storageConfig.restartRequired')}
              </div>
              <p className="text-sm text-muted-foreground">{t('storageConfig.restartDescription')}</p>
            </div>
            <Button type="button" onClick={onRestart} disabled={restarting}>
              {restarting ? <Loader2 className="size-4 animate-spin" /> : <Power className="size-4" />}
              {restarting ? t('storageConfig.actions.restarting') : t('storageConfig.actions.restart')}
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {loading ? (
        <Card className="rounded-lg">
          <CardHeader>
            <CardTitle>{t('storageConfig.currentConfig')}</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {t('storageConfig.loading')}
          </CardContent>
        </Card>
      ) : loadError || !config || !form ? (
        <Card className="rounded-lg">
          <CardHeader>
            <CardTitle>{t('storageConfig.loadFailed')}</CardTitle>
            <CardDescription>{loadError || t('common.serviceError')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => void loadConfig()}>{t('common.retry')}</Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card className="overflow-hidden rounded-lg border border-border/70 shadow-sm">
            <CardHeader className="border-b border-border/60 bg-muted/50">
              <CardTitle className="flex items-center gap-2 text-base">
                <Database className="size-4 text-primary" />
                {t('storageConfig.currentConfig')}
              </CardTitle>
              <CardDescription>{t('storageConfig.configSummary')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 pt-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="storage-backend">{t('storageConfig.fields.backend')}</Label>
                  <Select
                    value={form.backend}
                    onValueChange={(value) => updateForm('backend', value as StorageBackend)}
                  >
                    <SelectTrigger id="storage-backend" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="local">{t('storageConfig.backends.local')}</SelectItem>
                      <SelectItem value="s3">{t('storageConfig.backends.s3')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="storage-presign-ttl">{t('storageConfig.fields.presignTTL')}</Label>
                  <Input
                    id="storage-presign-ttl"
                    type="number"
                    min="1"
                    value={form.presign_ttl_sec}
                    onChange={(event) => updateForm('presign_ttl_sec', event.target.value)}
                  />
                </div>
              </div>

              {form.backend === 's3' ? (
                <section className="space-y-4" aria-labelledby="storage-s3-heading">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 id="storage-s3-heading" className="text-sm font-semibold">
                      {t('storageConfig.s3Title')}
                    </h2>
                    {config.s3_credentials_configured ? (
                      <Badge variant="secondary">{t('storageConfig.credentials.configured')}</Badge>
                    ) : null}
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="storage-s3-endpoint">{t('storageConfig.fields.s3Endpoint')}</Label>
                      <Input
                        id="storage-s3-endpoint"
                        value={form.s3_endpoint}
                        onChange={(event) => updateForm('s3_endpoint', event.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="storage-s3-region">{t('storageConfig.fields.s3Region')}</Label>
                      <Input
                        id="storage-s3-region"
                        value={form.s3_region}
                        onChange={(event) => updateForm('s3_region', event.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="storage-s3-access-key">{t('storageConfig.credentials.accessKey')}</Label>
                      <Input
                        id="storage-s3-access-key"
                        type="password"
                        autoComplete="new-password"
                        value={form.s3_access_key}
                        placeholder={t('storageConfig.credentials.keepPlaceholder')}
                        onChange={(event) => updateForm('s3_access_key', event.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="storage-s3-secret-key">{t('storageConfig.credentials.secretKey')}</Label>
                      <Input
                        id="storage-s3-secret-key"
                        type="password"
                        autoComplete="new-password"
                        value={form.s3_secret_key}
                        placeholder={t('storageConfig.credentials.keepPlaceholder')}
                        onChange={(event) => updateForm('s3_secret_key', event.target.value)}
                      />
                    </div>
                  </div>
                </section>
              ) : null}

              <section className="space-y-4" aria-labelledby="storage-buckets-heading">
                <h2 id="storage-buckets-heading" className="text-sm font-semibold">
                  {t('storageConfig.bucketsTitle')}
                </h2>
                <div className="grid gap-4 md:grid-cols-2">
                  {(Object.keys(form.buckets) as Array<keyof StorageConfigForm['buckets']>).map((key) => (
                    <div key={key} className="space-y-2">
                      <Label htmlFor={`storage-bucket-${key}`}>{t(`storageConfig.buckets.${key}`)}</Label>
                      <Input
                        id={`storage-bucket-${key}`}
                        value={form.buckets[key]}
                        onChange={(event) => updateBucket(key, event.target.value)}
                      />
                    </div>
                  ))}
                </div>
              </section>

              {form.backend === 'local' ? (
                <section className="space-y-4" aria-labelledby="storage-local-heading">
                  <h2 id="storage-local-heading" className="text-sm font-semibold">
                    {t('storageConfig.localRootsTitle')}
                  </h2>
                  <div className="grid gap-4 md:grid-cols-2">
                    {(Object.keys(form.local) as Array<keyof StorageConfigForm['local']>).map((key) => (
                      <div key={key} className="space-y-2">
                        <Label htmlFor={`storage-local-${key}`}>{t(LOCAL_ROOT_LABEL_KEYS[key])}</Label>
                        <Input
                          id={`storage-local-${key}`}
                          value={form.local[key]}
                          onChange={(event) => updateLocalRoot(key, event.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                </section>
              ) : null}

              <div className="flex flex-wrap items-center gap-2 border-t border-border/60 pt-5">
                <Button type="button" onClick={onSave} disabled={saving || resetting}>
                  {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
                  {saving ? t('storageConfig.actions.saving') : t('storageConfig.actions.save')}
                </Button>
                <Button type="button" variant="outline" onClick={onTest} disabled={testing || resetting}>
                  {testing ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
                  {testing ? t('storageConfig.actions.testing') : t('storageConfig.actions.test')}
                </Button>
                <Button type="button" variant="outline" onClick={onReset} disabled={resetting || saving || testing}>
                  {resetting ? <Loader2 className="size-4 animate-spin" /> : <RotateCcw className="size-4" />}
                  {resetting ? t('storageConfig.actions.resetting') : t('storageConfig.actions.reset')}
                </Button>
                {testResult ? (
                  <Badge variant={testResult.ok ? 'secondary' : 'destructive'} className="gap-1.5">
                    {testResult.ok ? (
                      <CheckCircle2 className="size-4 text-emerald-600" />
                    ) : (
                      <XCircle className="size-4" />
                    )}
                    {testResult.ok
                      ? t('storageConfig.testPassed')
                      : testResult.error || t('storageConfig.testFailed')}
                  </Badge>
                ) : null}
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <KeyRound className="size-4 text-primary" />
                {t('storageConfig.credentialTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
                {t('storageConfig.credentialDescription')}
              </p>
              <Badge variant="outline">
                {config.asset_signing_key_configured
                  ? t('storageConfig.signingKeyConfigured')
                  : t('storageConfig.signingKeyRandom')}
              </Badge>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
